/**
 * Project List Colouring — pure rule-evaluation engine + curated palette.
 *
 * SPEC-project-list-colour-rules / R2. Turns a tenant's rules config (from the
 * R1 endpoint `GET /api/me/settings/project-list-colors`) plus a single project
 * row into a per-slot visual treatment, then maps each {color, slot} pair to
 * concrete Tailwind classes.
 *
 * This module is PURE — no React, no network, no side effects — so it is cheap
 * to unit-test exhaustively. The applied components (ProjectsPage table rows,
 * ProjectMobileCardList cards) call `evaluateProjectColors` and render the
 * resolved classes/treatments.
 *
 * Inert by default: an empty `rules` array (or no rule matching a row) yields an
 * empty treatment object, so the list renders exactly as it does today.
 *
 * The wire types mirror R1's Pydantic models EXACTLY (see backend
 * routers/settings.py → ProjectListColorsConfig). Note the `in` operator's wire
 * value is the string `"in"` (not `"in_"`).
 */
import type { Project } from './schemas';

// ── Wire contract (mirrors R1 backend models) ───────────────────────────────

/** The visual treatment a rule targets. Slots are independent and layer. */
export type ColorSlot = 'border_left' | 'dot' | 'badge' | 'row_tint';

/** The project-list field a condition evaluates (client-side). */
export type ConditionField = 'category' | 'effective_fee' | 'workflow_stage';

/**
 * How a condition compares the field value against `value`.
 * NOTE: the wire value for set-membership is the string `"in"`.
 */
export type ConditionOperator =
  | 'equals'
  | 'in'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'between';

/** Curated named-swatch set. No free-form hex anywhere. Mirrors R1's PaletteColor. */
export type PaletteColor =
  | 'blue'
  | 'amber'
  | 'emerald'
  | 'teal'
  | 'violet'
  | 'rose'
  | 'slate'
  | 'indigo';

/** A single field/operator/value test. */
export interface Condition {
  field: ConditionField;
  operator: ConditionOperator;
  /** Scalar for equals/lt/lte/gt/gte; list for `in`; [min, max] for `between`. */
  value: unknown;
}

/** One colouring rule: when ALL conditions match, paint `color` in `slot`. */
export interface ColorRule {
  id: string;
  name: string;
  enabled: boolean;
  slot: ColorSlot;
  conditions: Condition[];
  color: PaletteColor;
  display_order: number;
}

/** The full per-tenant config persisted to Firestore (R1 GET response). */
export interface ProjectListColorsConfig {
  version: number;
  rules: ColorRule[];
}

/**
 * The resolved per-slot treatment for one row. Each present key holds the
 * winning rule's color for that slot. Absent keys mean "no treatment" — the
 * row renders as it does today for that slot.
 */
export interface ProjectColorTreatment {
  border_left?: PaletteColor;
  dot?: PaletteColor;
  badge?: PaletteColor;
  row_tint?: PaletteColor;
}

// ── effective fee ────────────────────────────────────────────────────────────

/** Workflow stages that are proposals (fee basis = proposed_total). */
const PROPOSAL_STAGES = new Set(['proposal_draft', 'proposal_sent']);

/**
 * Effective fee for a project row: `proposed_total` when the project is in a
 * proposal stage (`proposal_draft` / `proposal_sent`), else `confirmed_total`.
 *
 * Backs the `effective_fee` condition field so a single fee threshold classifies
 * a proposal by what's been proposed and a project by what's been confirmed.
 * Returns `undefined` when the relevant total is absent (treated as non-matching
 * by numeric comparisons).
 */
export function effectiveFee(
  project: Pick<Project, 'workflow_stage' | 'proposed_total' | 'confirmed_total'>,
): number | undefined {
  const stage = project.workflow_stage ?? '';
  if (PROPOSAL_STAGES.has(stage)) {
    return project.proposed_total;
  }
  return project.confirmed_total;
}

// ── condition evaluation ─────────────────────────────────────────────────────

/** Resolve the raw field value for a condition from a project row. */
function fieldValue(project: Project, field: ConditionField): unknown {
  switch (field) {
    case 'category':
      return project.category ?? null;
    case 'workflow_stage':
      return project.workflow_stage ?? null;
    case 'effective_fee':
      return effectiveFee(project);
    default:
      return undefined;
  }
}

/** Coerce to a finite number, or `undefined` when not comparable. */
function asNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return undefined;
}

/**
 * Evaluate a single condition against a project row. Unknown operators, missing
 * fields, or malformed `value` shapes never throw — they return `false` so a
 * partial/garbled rule simply doesn't match (keeping the feature inert).
 */
export function evaluateCondition(project: Project, condition: Condition): boolean {
  const actual = fieldValue(project, condition.field);
  const { operator, value } = condition;

  switch (operator) {
    case 'equals':
      return actual === value;

    case 'in':
      return Array.isArray(value) && value.some((v) => v === actual);

    case 'lt':
    case 'lte':
    case 'gt':
    case 'gte': {
      const a = asNumber(actual);
      const b = asNumber(value);
      if (a === undefined || b === undefined) return false;
      if (operator === 'lt') return a < b;
      if (operator === 'lte') return a <= b;
      if (operator === 'gt') return a > b;
      return a >= b; // gte
    }

    case 'between': {
      if (!Array.isArray(value) || value.length !== 2) return false;
      const a = asNumber(actual);
      const min = asNumber(value[0]);
      const max = asNumber(value[1]);
      if (a === undefined || min === undefined || max === undefined) return false;
      // Inclusive on both ends.
      return a >= min && a <= max;
    }

    default:
      return false;
  }
}

/** A rule matches only when ALL of its conditions pass (logical AND). */
export function ruleMatches(project: Project, rule: ColorRule): boolean {
  if (!Array.isArray(rule.conditions) || rule.conditions.length === 0) return false;
  return rule.conditions.every((c) => evaluateCondition(project, c));
}

// ── per-slot resolution ──────────────────────────────────────────────────────

const ALL_SLOTS: ColorSlot[] = ['border_left', 'dot', 'badge', 'row_tint'];

/**
 * Resolve the per-slot treatment for a project row.
 *
 * For EACH slot independently, the winner is the FIRST rule that is
 * `enabled === true`, targets that slot, and whose conditions ALL match — where
 * "first" is by ascending `display_order` (ties broken by original array order,
 * which is stable). Slots are resolved independently, so one row can receive a
 * border from one rule AND a dot from another.
 *
 * With an empty `rules` array (or no matching rule) the returned object is empty
 * `{}` — the inert default that leaves the list visually unchanged.
 */
export function evaluateProjectColors(
  project: Project,
  rules: ColorRule[] | undefined | null,
): ProjectColorTreatment {
  const treatment: ProjectColorTreatment = {};
  if (!Array.isArray(rules) || rules.length === 0) return treatment;

  // Stable ascending sort by display_order. Array#sort is stable in modern
  // engines, so equal display_order keeps config order.
  const ordered = [...rules].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
  );

  for (const slot of ALL_SLOTS) {
    const winner = ordered.find(
      (rule) => rule.enabled === true && rule.slot === slot && ruleMatches(project, rule),
    );
    if (winner) {
      treatment[slot] = winner.color;
    }
  }

  return treatment;
}

// ── curated palette → Tailwind classes ───────────────────────────────────────

/**
 * PALETTE[color][slot] → concrete Tailwind class string.
 *
 * - border_left → a 4px coloured left border on the row.
 * - row_tint    → a subtle bg-*-50 row background.
 * - dot         → text colour for a ● glyph rendered inline.
 * - badge       → bg/text classes for an inline pill.
 *
 * Mirrors the existing colour-map helpers (BillableItemsTable.tsx
 * getStatusColor/getCategoryColor). An unknown colour or slot yields the empty
 * string (no class) and never throws — see `paletteClass`.
 */
export const PALETTE: Record<PaletteColor, Record<ColorSlot, string>> = {
  blue: {
    border_left: 'border-l-4 border-blue-500',
    row_tint: 'bg-blue-50',
    dot: 'text-blue-500',
    badge: 'bg-blue-50 text-blue-700',
  },
  amber: {
    border_left: 'border-l-4 border-amber-500',
    row_tint: 'bg-amber-50',
    dot: 'text-amber-500',
    badge: 'bg-amber-50 text-amber-700',
  },
  emerald: {
    border_left: 'border-l-4 border-emerald-500',
    row_tint: 'bg-emerald-50',
    dot: 'text-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700',
  },
  teal: {
    border_left: 'border-l-4 border-teal-500',
    row_tint: 'bg-teal-50',
    dot: 'text-teal-500',
    badge: 'bg-teal-50 text-teal-700',
  },
  violet: {
    border_left: 'border-l-4 border-violet-500',
    row_tint: 'bg-violet-50',
    dot: 'text-violet-500',
    badge: 'bg-violet-50 text-violet-700',
  },
  rose: {
    border_left: 'border-l-4 border-rose-500',
    row_tint: 'bg-rose-50',
    dot: 'text-rose-500',
    badge: 'bg-rose-50 text-rose-700',
  },
  slate: {
    border_left: 'border-l-4 border-slate-500',
    row_tint: 'bg-slate-50',
    dot: 'text-slate-500',
    badge: 'bg-slate-100 text-slate-700',
  },
  indigo: {
    border_left: 'border-l-4 border-indigo-500',
    row_tint: 'bg-indigo-50',
    dot: 'text-indigo-500',
    badge: 'bg-indigo-50 text-indigo-700',
  },
};

/**
 * Look up the Tailwind class string for a {color, slot} pair. Returns the empty
 * string for an unknown/undefined colour or slot — never throws — so callers can
 * splat the result into `cn()` safely.
 */
export function paletteClass(
  color: PaletteColor | undefined,
  slot: ColorSlot,
): string {
  if (!color) return '';
  const entry = PALETTE[color];
  if (!entry) return '';
  return entry[slot] ?? '';
}

/**
 * Convenience for row-level classes: returns the border_left + row_tint classes
 * for a resolved treatment, joined with a space (empty string when neither
 * applies). Safe to drop into `cn()`.
 */
export function rowTreatmentClasses(treatment: ProjectColorTreatment): string {
  return [
    paletteClass(treatment.border_left, 'border_left'),
    paletteClass(treatment.row_tint, 'row_tint'),
  ]
    .filter(Boolean)
    .join(' ');
}
